const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const mapPropertiesToBusinessUnits = (properties) =>
  (Array.isArray(properties) ? properties : [])
    .filter((p) => p.businessUnitId)
    .map((p) => ({
      businessUnitId: p.businessUnitId,
      businessUnitName: p.propertyName,
      businessUnitCode: p.propertyCode,
      propertyId: p.propertyId,
      parentBusinessUnitId: null,
      status: p.status || 'ACTIVE',
    }));

const fetchPropertyBusinessUnits = async (tenantId, { scope = 'user' } = {}) => {
  if (!tenantId) return [];

  const properties = await apiFetch(`conductor/cash-recon/properties/by-tenant?tenant_id=${tenantId}`);
  const list = Array.isArray(properties) ? properties : [];

  if (scope === 'tenant') {
    return mapPropertiesToBusinessUnits(list);
  }

  const userId = getUserId();
  if (!userId) {
    return mapPropertiesToBusinessUnits(list);
  }

  const userProps = await apiFetch(
    `conductor/user-tenant-properties?tenant_id=${tenantId}&user_id=${userId}`
  );
  const allowedPropertyIds = new Set(
    (Array.isArray(userProps) ? userProps : []).map((p) => p.propertyId)
  );

  return mapPropertiesToBusinessUnits(
    list.filter((p) => p.propertyId && allowedPropertyIds.has(p.propertyId))
  );
};

export const buildExpenseDistributionFromLines = (lines = []) =>
  lines.map((line, index) => ({
    id: line.apInvoiceLineId || `line-${index}`,
    description: line.lineItemDescription || '',
    businessUnitId: line.businessUnitId,
    glAccountId: line.glAccountId,
    budgetCodeId: line.budgetCodeId,
    purchaseOrder: line.purchaseOrder || '',
    amount: Number(line.lineItemTotal) || 0,
    isSplit: Boolean(line.isSplit),
  }));

export const enrichExpenseRow = (row, lookups = {}) => {
  const buMap = lookups.businessUnits || {};
  const glMap = lookups.glAccounts || {};
  const budgetMap = lookups.budgetCodes || {};

  return {
    ...row,
    businessUnitName: buMap[row.businessUnitId] || '',
    glAccount: glMap[row.glAccountId] || '',
    budgetCode: budgetMap[row.budgetCodeId] || '',
  };
};

const PARSER_EMPTY_VALUES = new Set(['none', 'null', 'n/a', 'na', '-']);

export const isParserEmptyValue = (value) => {
  if (value == null) return true;
  const text = String(value).trim();
  if (!text) return true;
  return PARSER_EMPTY_VALUES.has(text.toLowerCase());
};

export const normalizeText = (value) => {
  if (isParserEmptyValue(value)) return '';
  return String(value).trim().toLowerCase().replace(/\s+/g, ' ');
};

export const normalizePhone = (value) => {
  if (isParserEmptyValue(value)) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1);
  }
  return digits;
};

export const normalizeAddress = (value) => {
  if (isParserEmptyValue(value)) return '';
  return String(value)
    .toLowerCase()
    .replace(/\r/g, '\n')
    .replace(/[^\w\s,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const normalizeEmail = (value) => {
  if (isParserEmptyValue(value)) return '';
  return String(value).trim().toLowerCase();
};

const parseInvoiceContentRow = (content) => {
  if (!content) return null;
  try {
    const parsed = typeof content === 'string' ? JSON.parse(content) : content;
    if (Array.isArray(parsed) && parsed.length > 0) return parsed[0];
    return null;
  } catch {
    return null;
  }
};

export const extractVendorFieldsFromInvoice = (invoice) => {
  if (!invoice) {
    return { vendorName: '', vendorAddress: '', vendorPhone: '', vendorEmail: '' };
  }

  const contentRow = parseInvoiceContentRow(invoice.content);
  const pick = (...values) => {
    for (const value of values) {
      if (!isParserEmptyValue(value)) return String(value).trim();
    }
    return '';
  };

  return {
    vendorName: pick(invoice.vendorName, contentRow?.VENDOR_NAME, contentRow?.SUPPLIER_NAME),
    vendorAddress: pick(invoice.vendorAddress, contentRow?.VENDOR_ADDRESS),
    vendorPhone: pick(invoice.vendorPhone, contentRow?.VENDOR_PHONE),
    vendorEmail: pick(invoice.vendorEmail, contentRow?.VENDOR_EMAIL),
  };
};

export const getActiveVendors = (vendors = []) => (
  (Array.isArray(vendors) ? vendors : []).filter((vendor) => {
    const status = String(vendor?.status || 'ACTIVE').toUpperCase();
    return status === 'ACTIVE';
  })
);

export const resolveVendorMasterRecord = (invoice, vendors = []) => {
  const activeVendors = getActiveVendors(vendors);
  if (!activeVendors.length || !invoice) return null;

  if (invoice.vendorId) {
    const linked = activeVendors.find(
      (vendor) => String(vendor.vendorId) === String(invoice.vendorId)
    );
    if (linked) return linked;
  }

  const { vendorName } = extractVendorFieldsFromInvoice(invoice);
  const normalizedName = normalizeText(vendorName);
  if (!normalizedName) return null;

  return activeVendors.find(
    (vendor) => normalizeText(vendor.vendorName) === normalizedName
  ) || null;
};

const buildVendorNameValidation = (invoice, vendors) => {
  const documentType = documentTypeFromContent(invoice?.content);
  if (!mathematicalIntegrityApplies(documentType)) {
    return {
      id: 'vendorName',
      label: 'Vendor Name Match',
      description: 'Compare parsed vendor name against approved Vendor Master records.',
      status: 'SKIP',
      detail: `Not required when document type is ${documentType}.`,
    };
  }

  const activeVendors = getActiveVendors(vendors);
  if (!activeVendors.length) {
    return {
      id: 'vendorName',
      label: 'Vendor Name Match',
      description: 'Compare parsed vendor name against approved Vendor Master records.',
      status: 'SKIP',
      detail: 'No active vendors in Vendor Master — check skipped.',
    };
  }

  const { vendorName } = extractVendorFieldsFromInvoice(invoice);
  if (!vendorName) {
    return {
      id: 'vendorName',
      label: 'Vendor Name Match',
      description: 'Compare parsed vendor name against approved Vendor Master records.',
      status: 'FAIL',
      detail: 'No vendor name found on the invoice.',
    };
  }

  const masterVendor = resolveVendorMasterRecord(invoice, activeVendors);
  if (!masterVendor) {
    return {
      id: 'vendorName',
      label: 'Vendor Name Match',
      description: 'Compare parsed vendor name against approved Vendor Master records.',
      status: 'FAIL',
      detail: `Invoice vendor "${vendorName}" was not found in Vendor Master.`,
    };
  }

  const invoiceName = normalizeText(vendorName);
  const masterName = normalizeText(masterVendor.vendorName);
  const nameMatches = invoiceName === masterName;

  return {
    id: 'vendorName',
    label: 'Vendor Name Match',
    description: 'Compare parsed vendor name against approved Vendor Master records.',
    status: nameMatches ? 'PASS' : 'FAIL',
    detail: nameMatches
      ? `Matches Vendor Master record "${masterVendor.vendorName}".`
      : `Invoice shows "${vendorName}" but Vendor Master has "${masterVendor.vendorName}".`,
  };
};

const buildVendorAddressValidation = (invoice, vendors) => {
  const activeVendors = getActiveVendors(vendors);
  const { vendorAddress } = extractVendorFieldsFromInvoice(invoice);

  if (!vendorAddress) {
    return {
      id: 'vendorAddress',
      label: 'Vendor Address Match',
      description: 'Compare parsed vendor address against the address on file in Vendor Master.',
      status: 'SKIP',
      detail: 'No vendor address found on the invoice.',
    };
  }

  const masterVendor = resolveVendorMasterRecord(invoice, activeVendors);
  if (!masterVendor) {
    return {
      id: 'vendorAddress',
      label: 'Vendor Address Match',
      description: 'Compare parsed vendor address against the address on file in Vendor Master.',
      status: 'SKIP',
      detail: 'Vendor is not linked to Vendor Master — address check skipped.',
    };
  }

  if (isParserEmptyValue(masterVendor.address)) {
    return {
      id: 'vendorAddress',
      label: 'Vendor Address Match',
      description: 'Compare parsed vendor address against the address on file in Vendor Master.',
      status: 'SKIP',
      detail: `No address on file for "${masterVendor.vendorName}".`,
    };
  }

  const invoiceAddress = normalizeAddress(vendorAddress);
  const masterAddress = normalizeAddress(masterVendor.address);
  const addressMatches = invoiceAddress === masterAddress
    || (invoiceAddress && masterAddress && (
      invoiceAddress.includes(masterAddress) || masterAddress.includes(invoiceAddress)
    ));

  return {
    id: 'vendorAddress',
    label: 'Vendor Address Match',
    description: 'Compare parsed vendor address against the address on file in Vendor Master.',
    status: addressMatches ? 'PASS' : 'WARN',
    detail: addressMatches
      ? 'Invoice vendor address matches Vendor Master.'
      : `Invoice address does not match Vendor Master for "${masterVendor.vendorName}".`,
  };
};

const buildVendorPhoneValidation = (invoice, vendors) => {
  const activeVendors = getActiveVendors(vendors);
  const { vendorPhone } = extractVendorFieldsFromInvoice(invoice);

  if (!vendorPhone) {
    return {
      id: 'vendorPhone',
      label: 'Vendor Phone Match',
      description: 'Compare parsed vendor phone against contact number on file in Vendor Master.',
      status: 'SKIP',
      detail: 'No vendor phone found on the invoice.',
    };
  }

  const masterVendor = resolveVendorMasterRecord(invoice, activeVendors);
  if (!masterVendor) {
    return {
      id: 'vendorPhone',
      label: 'Vendor Phone Match',
      description: 'Compare parsed vendor phone against contact number on file in Vendor Master.',
      status: 'SKIP',
      detail: 'Vendor is not linked to Vendor Master — phone check skipped.',
    };
  }

  const masterPhone = masterVendor.contactNumber;
  if (isParserEmptyValue(masterPhone)) {
    return {
      id: 'vendorPhone',
      label: 'Vendor Phone Match',
      description: 'Compare parsed vendor phone against contact number on file in Vendor Master.',
      status: 'SKIP',
      detail: `No contact number on file for "${masterVendor.vendorName}".`,
    };
  }

  const invoiceDigits = normalizePhone(vendorPhone);
  const masterDigits = normalizePhone(masterPhone);
  const phoneMatches = Boolean(invoiceDigits && masterDigits && invoiceDigits === masterDigits);

  return {
    id: 'vendorPhone',
    label: 'Vendor Phone Match',
    description: 'Compare parsed vendor phone against contact number on file in Vendor Master.',
    status: phoneMatches ? 'PASS' : 'WARN',
    detail: phoneMatches
      ? 'Invoice vendor phone matches Vendor Master contact number.'
      : `Invoice phone (${vendorPhone}) does not match Vendor Master (${masterPhone}).`,
  };
};

const buildVendorEmailValidation = (invoice, vendors) => {
  const activeVendors = getActiveVendors(vendors);
  const { vendorEmail } = extractVendorFieldsFromInvoice(invoice);

  if (!vendorEmail) {
    return {
      id: 'vendorEmail',
      label: 'Vendor Email Match',
      description: 'Compare parsed vendor email against email ID on file in Vendor Master.',
      status: 'SKIP',
      detail: 'No vendor email found on the invoice.',
    };
  }

  const masterVendor = resolveVendorMasterRecord(invoice, activeVendors);
  if (!masterVendor) {
    return {
      id: 'vendorEmail',
      label: 'Vendor Email Match',
      description: 'Compare parsed vendor email against email ID on file in Vendor Master.',
      status: 'SKIP',
      detail: 'Vendor is not linked to Vendor Master — email check skipped.',
    };
  }

  const masterEmail = masterVendor.emailId;
  if (isParserEmptyValue(masterEmail)) {
    return {
      id: 'vendorEmail',
      label: 'Vendor Email Match',
      description: 'Compare parsed vendor email against email ID on file in Vendor Master.',
      status: 'SKIP',
      detail: `No email ID on file for "${masterVendor.vendorName}".`,
    };
  }

  const invoiceEmail = normalizeEmail(vendorEmail);
  const masterEmailNormalized = normalizeEmail(masterEmail);
  const emailMatches = Boolean(
    invoiceEmail && masterEmailNormalized && invoiceEmail === masterEmailNormalized
  );

  return {
    id: 'vendorEmail',
    label: 'Vendor Email Match',
    description: 'Compare parsed vendor email against email ID on file in Vendor Master.',
    status: emailMatches ? 'PASS' : 'WARN',
    detail: emailMatches
      ? 'Invoice vendor email matches Vendor Master email ID.'
      : `Invoice email (${vendorEmail}) does not match Vendor Master (${masterEmail}).`,
  };
};

const missingDocumentType = (value) => {
  const text = String(value ?? '').trim().toLowerCase();
  return !text || text === 'none' || text === 'null' || text === 'n/a';
};

/** First real DOCUMENT_TYPE on parser content. Missing, blank, and None stay unset. */
export const documentTypeFromContent = (content) => {
  if (content == null || content === '') return null;
  let rows = content;
  if (typeof content === 'string') {
    try {
      rows = JSON.parse(content);
    } catch {
      return null;
    }
  }
  if (rows && typeof rows === 'object' && !Array.isArray(rows)) rows = [rows];
  if (!Array.isArray(rows)) return null;
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const raw = row.DOCUMENT_TYPE ?? row.documentType ?? row.document_type;
    if (missingDocumentType(raw)) continue;
    return String(raw).trim();
  }
  return null;
};

/** Math still applies to Invoice, Receipt, and documents with no type. */
export const mathematicalIntegrityApplies = (documentType) => {
  if (missingDocumentType(documentType)) return true;
  const normalized = String(documentType).trim().toLowerCase();
  return normalized === 'invoice' || normalized === 'receipt';
};

export const runValidations = (invoice, expenseLines, vendors = []) => {
  if (!invoice) return [];
  const lineSum = expenseLines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const tax = Number(invoice.invoiceTotalTaxes) || 0;
  const shipping = Number(invoice.invoiceTotalFees) || 0;
  const total = Number(invoice.invoiceTotalAmountDue) || 0;
  const calculated = lineSum + tax + shipping;
  const mathOk = Math.abs(calculated - total) < 0.01;
  const documentType = documentTypeFromContent(invoice.content);
  const mathApplies = mathematicalIntegrityApplies(documentType);
  const duplicateOk = !invoice.isDuplicate;
  const poReference = invoice.purchaseOrderNumber || invoice.poReference || '';
  const hasPo = Boolean(poReference.trim());
  const threeWayOk = !hasPo || expenseLines.every((l) => l.purchaseOrder === poReference);

  return [
    {
      id: 'duplicate',
      label: 'Duplicate Check',
      description: 'Query for existing Invoice Number + Business Unit to prevent double payment.',
      status: duplicateOk ? 'PASS' : 'FAIL',
      detail: duplicateOk
        ? 'No duplicate invoice found for this business unit.'
        : 'Duplicate detected for this business unit and invoice number.',
    },
    buildVendorNameValidation(invoice, vendors),
    buildVendorAddressValidation(invoice, vendors),
    buildVendorPhoneValidation(invoice, vendors),
    buildVendorEmailValidation(invoice, vendors),
    {
      id: 'math',
      label: 'Mathematical Integrity',
      description: '(Sum of Line Items) + Tax + Shipping must equal Total Amount.',
      status: mathApplies ? (mathOk ? 'PASS' : 'FAIL') : 'SKIP',
      detail: !mathApplies
        ? `Not required when document type is ${documentType}.`
        : mathOk
          ? `Lines (${lineSum.toFixed(2)}) + Tax (${tax.toFixed(2)}) + Shipping (${shipping.toFixed(2)}) = ${calculated.toFixed(2)}`
          : `Expected ${calculated.toFixed(2)} but total is ${total.toFixed(2)}`,
    },
    {
      id: 'threeWay',
      label: '3-Way Matching',
      description: hasPo
        ? 'Match line items against PO and Goods Received Note (GRN) in ERP.'
        : 'No PO reference — 3-way matching skipped.',
      status: !hasPo ? 'SKIP' : (threeWayOk ? 'PASS' : 'WARN'),
      detail: !hasPo
        ? 'Optional validation not applicable.'
        : threeWayOk
          ? `All lines matched to ${poReference}.`
          : 'One or more lines do not match the PO reference.',
    },
  ];
};

export const validationStatusColor = (status) => {
  if (status === 'PASS') return 'success';
  if (status === 'FAIL') return 'error';
  if (status === 'WARN') return 'warning';
  return 'default';
};

/** Resolve propertyId for an invoice/line business unit (property.businessUnitId or propertyId). */
export const getPropertyIdForBusinessUnit = (businessUnits = [], businessUnitId) => {
  if (!businessUnitId) return null;
  const match = (Array.isArray(businessUnits) ? businessUnits : []).find(
    (u) =>
      String(u.businessUnitId) === String(businessUnitId) ||
      String(u.propertyId) === String(businessUnitId)
  );
  return match?.propertyId || match?.businessUnitId || null;
};

/** GL accounts linked to a specific property only (not all tenant GLs). */
export const filterGlAccountsByProperty = (glAccounts = [], propertyId) => {
  if (!propertyId) return [];
  return (Array.isArray(glAccounts) ? glAccounts : []).filter(
    (g) => g.propertyId && String(g.propertyId) === String(propertyId)
  );
};

export const filterGlAccountsForBusinessUnit = (
  glAccounts = [],
  businessUnits = [],
  businessUnitId,
  selectedGlAccountId = null
) => {
  const filtered = filterGlAccountsByProperty(
    glAccounts,
    getPropertyIdForBusinessUnit(businessUnits, businessUnitId)
  );

  if (!selectedGlAccountId) return filtered;

  const list = Array.isArray(glAccounts) ? glAccounts : [];
  const selected = list.find((account) => String(account.ledgerAccountId) === String(selectedGlAccountId));
  if (selected && !filtered.some((account) => String(account.ledgerAccountId) === String(selectedGlAccountId))) {
    return [...filtered, selected];
  }

  return filtered;
};

export const fetchExpenseLookupMaps = async (tenantId) => {
  const [businessUnits, glAccounts, budgetCodes] = await Promise.all([
    fetchPropertyBusinessUnits(tenantId, { scope: 'user' }),
    apiFetch(`conductor/cash-recon/chart-of-account/by-tenant?tenant_id=${tenantId}`).catch(() => []),
    apiFetch(`conductor/budget-codes/tenant/${tenantId}`).catch(() => []),
  ]);

  return {
    businessUnitList: businessUnits,
    businessUnits: Object.fromEntries(
      businessUnits.map((u) => [u.businessUnitId, u.businessUnitName])
    ),
    glAccounts: Object.fromEntries(
      (Array.isArray(glAccounts) ? glAccounts : []).map((g) => [
        g.ledgerAccountId,
        `${g.glCode || ''}${g.glDescription ? ` - ${g.glDescription}` : ''}`.trim(),
      ])
    ),
    budgetCodes: Object.fromEntries(
      (Array.isArray(budgetCodes) ? budgetCodes : []).map((b) => [b.budgetCodeId, b.code])
    ),
  };
};

export const parseApInvoiceHeaderAdditionalData = (header) => {
  try {
    const raw = header?.headerAdditionalData;
    if (!raw) return {};
    return typeof raw === 'string' ? JSON.parse(raw) : { ...raw };
  } catch {
    return {};
  }
};

/** Returns a failed approval attempt from header metadata, if any. */
export const getLastApprovalAttempt = (header) => {
  const meta = parseApInvoiceHeaderAdditionalData(header);
  const attempt = meta.lastApprovalAttempt;
  if (!attempt || attempt.success !== false) return null;
  const reason = String(attempt.reason || '').trim();
  if (!reason) return null;
  return attempt;
};

export const getLastApprovalFailureReason = (header) => getLastApprovalAttempt(header)?.reason || '';

export const buildHeaderAdditionalDataWithApprovalAttempt = (header, attempt) => {
  const meta = parseApInvoiceHeaderAdditionalData(header);
  if (attempt == null) {
    const { lastApprovalAttempt, ...rest } = meta;
    return JSON.stringify(rest);
  }
  return JSON.stringify({ ...meta, lastApprovalAttempt: attempt });
};

export const buildFailedApprovalAttempt = (reason, { userId = null, failedChecks = null } = {}) => ({
  success: false,
  reason: String(reason || 'Approval failed').trim(),
  failedChecks: Array.isArray(failedChecks) ? failedChecks : null,
  timestamp: new Date().toISOString(),
  userId: userId || null,
});

export const TRUSTED_VENDOR_AUTO_APPROVE_COMMENT = 'Auto-approved — trusted vendor';

/** True when header workflow history shows trusted-vendor auto-approve. */
export const wasTrustedVendorAutoApproved = (header) => {
  const meta = parseApInvoiceHeaderAdditionalData(header);
  const history = Array.isArray(meta.workflowHistory) ? meta.workflowHistory : [];
  return history.some((entry) => {
    const text = String(entry?.comment || entry?.action || '');
    return text.includes(TRUSTED_VENDOR_AUTO_APPROVE_COMMENT) || entry?.source === 'TRUSTED_VENDOR';
  });
};
